import { HTTPError } from "@marketplace/http";
import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Text
} from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useAtom, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { HiPlus } from "react-icons/hi";

import { NoVoucherIllustraction } from "@/assets/images/no-voucher-illustration";
import { Pagination } from "@/components";

import { createVoucherModalAtom, deleteVoucherModalAtom } from "../../atoms";
import { CreateVoucherModal } from "../../components/create-voucher-modal";
import { DeleteVoucherModal } from "../../components/delete-voucher-modal";
import { Filters } from "../../components/filters";
import { VoucherApprovalModal } from "../../components/voucher-approval-modal";
import { VoucherListTable } from "../../components/voucher-list-table";
import { useGetVoucherListQuery } from "../../hooks/queries/use-get-voucher-list-query";

const route = getRouteApi("/vouchers/");

type NoVoucherProps = {
  onAddVoucher: () => void;
};

const NoVoucher: React.FC<NoVoucherProps> = ({ onAddVoucher }) => {
  const { t } = useTranslation("voucher");

  return (
    <div className="flex flex-col justify-center items-center gap-y-6 mt-[56px]">
      <NoVoucherIllustraction />
      <div className="text-center">
        <Text type="heading" size="xl" className="mb-2 text-fjord-grey-500">
          {t("voucherListPage.emptyVoucher.title")}
        </Text>
        <Text type="paragraph" size="l" className="text-fjord-grey-300">
          {t("voucherListPage.emptyVoucher.description")}
        </Text>
      </div>
      <Button
        variant="primary"
        onClick={onAddVoucher}
        rightSection={<HiPlus size={16} />}
      >
        {t("voucherListPage.addVoucher")}
      </Button>
    </div>
  );
};

export const VoucherListPage = () => {
  const { page, q, sort } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("voucher");
  const setCreateVoucherModal = useSetAtom(createVoucherModalAtom);
  const [deleteVoucherModal, setDeleteVoucherModal] = useAtom(
    deleteVoucherModalAtom
  );

  const { data, isLoading, isError, error } = useGetVoucherListQuery({
    queryParams: {
      page,
      limit: 10,
      q,
      sort
    }
  });

  const handleAddVoucher = () => {
    setCreateVoucherModal({ visible: true });
  };

  const voucherListData = data?.data;
  const metadata = data?.metadata;

  return (
    <>
      <CreateVoucherModal />
      <VoucherApprovalModal />
      <DeleteVoucherModal
        opened={deleteVoucherModal.visible}
        onClose={() =>
          setDeleteVoucherModal({
            visible: false,
            data: null
          })
        }
      />
      <div className="flex justify-between items-center mb-6">
        <Text type="heading" size="l" className="text-humble-blue-500">
          {t("voucherListPage.title")}
        </Text>
        <Button
          variant="primary"
          rightSection={<HiPlus size={16} />}
          onClick={handleAddVoucher}
        >
          {t("voucherListPage.addVoucher")}
        </Button>
      </div>

      <Filters />

      <ConditionalRendering
        condition={voucherListData && voucherListData.length > 0}
      >
        <div className="space-y-6">
          <VoucherListTable records={voucherListData ?? []} />

          <Pagination
            currentPageTotal={voucherListData?.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: {
                  page: value
                }
              })
            }
          />
        </div>
      </ConditionalRendering>

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      {/* Show NoVoucher if 404 */}
      <ConditionalRendering
        condition={
          isError &&
          error instanceof HTTPError &&
          error.response?.status === 404
        }
      >
        <NoVoucher onAddVoucher={handleAddVoucher} />
      </ConditionalRendering>

      {/* Show NoVoucher if empty data but not error */}
      <ConditionalRendering
        condition={!isError && voucherListData && voucherListData?.length === 0}
      >
        <NoVoucher onAddVoucher={handleAddVoucher} />
      </ConditionalRendering>
    </>
  );
};
